import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import type { Campaign, Suggestion, VoteOption } from '../store/appStore';
import { Button } from '../components/ui/button';
import { DEMOKRATIUKA_LOCATIONS, getLocationLabel } from '../lib/demokratiukaBrand';

const TYPE_LABELS: Record<string, string> = {
  poster: 'Plakat',
  debate: 'Debatt',
  vote: 'Avstemning',
};

// Plakat-kampanjer som IKKE har per-lokasjon QR-koder (én felles QR i stedet)
const SINGLE_QR_POSTER_SLUGS = new Set<string>(['danby-choi-sporsmal', 'alexandra-fredwall-sporsmal']);

// Kampanjer der det finnes en alternativ rød QR-variant (filsuffix --rod.png)
const RED_VARIANT_SLUGS = new Set<string>(['steinfeld-russland']);

export const CampaignDetail: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const {
    currentAccount,
    fetchCampaignSuggestions,
    updateCampaign,
    deleteCampaign,
    fetchVoteOptions,
    saveVoteOptions,
    deleteSuggestion,
    deleteCampaignSuggestions,
    toggleSuggestionUsed,
  } = useAppStore();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [siblings, setSiblings] = useState<Campaign[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  // Filter-state: null = vis alle lokasjoner (aggregat)
  const [activeLocation, setActiveLocation] = useState<string | null>(null);
  // Valgt QR-lokasjon i QR-boksen (default første i lista)
  const [qrLocation, setQrLocation] = useState<string>(DEMOKRATIUKA_LOCATIONS[0].slug);
  const [qrColor, setQrColor] = useState<'blue' | 'red'>('blue');

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editOptions, setEditOptions] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = async () => {
    if (!currentAccount || !campaignId) return;
    const { fetchCampaignsByAccount } = useAppStore.getState();
    const campaigns = await fetchCampaignsByAccount(currentAccount.id).catch(() => []);
    const found = campaigns.find(c => c.id === campaignId);
    if (!found) {
      navigate('/dashboard');
      return;
    }
    setCampaign(found);
    // Samle søskenkampanjer av samme type for prev/next-navigasjon
    setSiblings(campaigns.filter(c => c.type === found.type));
    const sug = await fetchCampaignSuggestions(campaignId).catch(() => []);
    setSuggestions(sug);
    setIsLoading(false);
  };

  useEffect(() => {
    load();
    // Nullstill lokasjonsfilter når man bytter kampanje
    setActiveLocation(null);
    setQrLocation(DEMOKRATIUKA_LOCATIONS[0].slug);
  }, [campaignId, currentAccount?.id]);

  // Filtrer svar etter aktivt lokasjonsvalg (null = alle)
  const filteredSuggestions = useMemo(() => {
    if (activeLocation === null) return suggestions;
    return suggestions.filter(s => s.location === activeLocation);
  }, [suggestions, activeLocation]);

  // Antall svar per lokasjon (for chips-visning)
  const locationCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of suggestions) {
      if (s.location) counts[s.location] = (counts[s.location] ?? 0) + 1;
    }
    return counts;
  }, [suggestions]);

  // Forrige / neste kampanje av samme type (for navigasjon)
  const currentIndex = campaign ? siblings.findIndex(c => c.id === campaign.id) : -1;
  const prevSibling = currentIndex > 0 ? siblings[currentIndex - 1] : null;
  const nextSibling = currentIndex >= 0 && currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] : null;

  const handleRefresh = async () => {
    if (!campaignId) return;
    setIsRefreshing(true);
    const sug = await fetchCampaignSuggestions(campaignId).catch(() => []);
    setSuggestions(sug);
    setIsRefreshing(false);
  };

  const startEdit = async () => {
    if (!campaign) return;
    setEditTitle(campaign.title);
    setEditDesc(campaign.description || '');
    if (campaign.type === 'vote') {
      const opts = await fetchVoteOptions(campaign.id).catch(() => []);
      setEditOptions(opts.map((o: VoteOption) => o.label).join('\n'));
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!campaign) return;
    setIsSaving(true);
    try {
      await updateCampaign(campaign.id, {
        title: editTitle.trim(),
        description: editDesc.trim() || null,
      });
      if (campaign.type === 'vote') {
        const labels = editOptions.split('\n').map(l => l.trim()).filter(Boolean);
        await saveVoteOptions(campaign.id, labels);
      }
      setIsEditing(false);
      await load();
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleClose = async () => {
    if (!campaign) return;
    const newClosesAt = campaign.closesAt ? null : new Date().toISOString();
    await updateCampaign(campaign.id, { closesAt: newClosesAt });
    await load();
  };

  const handleDelete = async () => {
    if (!campaign) return;
    if (!confirm(`Slett kampanjen "${campaign.title}"? Dette kan ikke angres.`)) return;
    await deleteCampaign(campaign.id);
    navigate('/dashboard');
  };

  const handleToggleUsed = async (suggestion: Suggestion) => {
    await toggleSuggestionUsed(suggestion.id, suggestion.used);
    setSuggestions(prev =>
      prev.map(s => s.id === suggestion.id ? { ...s, used: !s.used } : s)
    );
  };

  const handleDeleteSuggestion = async (suggestion: Suggestion) => {
    await deleteSuggestion(suggestion.id);
    setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
  };

  const handleDeleteAllSuggestions = async () => {
    if (!campaign) return;
    if (!confirm(`Slett alle ${suggestions.length} svar? Dette kan ikke angres.`)) return;
    await deleteCampaignSuggestions(campaign.id);
    setSuggestions([]);
  };

  if (!currentAccount) {
    navigate('/signin');
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-gray-400">Laster kampanje...</p>
      </div>
    );
  }

  if (!campaign) return null;

  const isClosed = !!campaign.closesAt;
  const campaignUrl = `https://scenechat.no/request/${currentAccount.handle}/${campaign.slug}`;

  // Fullscreen mode for suggestions
  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0a0a0a] flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-white/10">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-white truncate">{campaign.title}</h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-white/50 text-sm">{suggestions.length} svar</span>
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="bg-white/10 hover:bg-white/20 text-white text-sm"
            >
              {isRefreshing ? '...' : 'Oppdater'}
            </Button>
            <Button
              onClick={() => setFullscreen(false)}
              className="bg-white/10 hover:bg-white/20 text-white"
            >
              Lukk fullskjerm
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-8">
          <div className="space-y-2 max-w-4xl mx-auto">
            {suggestions.length === 0 ? (
              <p className="text-gray-500 text-center py-16 text-xl">Ingen svar ennå.</p>
            ) : (
              suggestions.map((s, i) => (
                <div
                  key={s.id}
                  className={`p-5 rounded-lg ${s.used ? 'bg-[#1f2a1f] border border-green-500/20' : 'bg-[#1a1a1a]'}`}
                >
                  <div className="flex items-start gap-4">
                    <span className="text-2xl text-white/30 font-bold tabular-nums w-12 shrink-0">
                      #{suggestions.length - i}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xl leading-relaxed ${s.used ? 'text-gray-500 line-through' : 'text-white'}`}>
                        {s.message}
                      </p>
                      <p className="text-gray-500 text-sm mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {s.submittedBy && <span className="text-gray-400">{s.submittedBy}</span>}
                        {s.submittedBy && <span>&middot;</span>}
                        <span>
                          {new Date(s.createdAt).toLocaleDateString('no-NO', { day: 'numeric', month: 'short' })} &middot;{' '}
                          {new Date(s.createdAt).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {s.location && (
                          <span className="text-blue-300 bg-blue-500/10 px-2 py-0.5 rounded text-xs">
                            {getLocationLabel(s.location)}
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleUsed(s)}
                      className={`text-sm px-4 py-2 rounded shrink-0 transition-colors ${
                        s.used
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-white hover:bg-gray-200 text-black'
                      }`}
                    >
                      {s.used ? 'Brukt' : 'Merk brukt'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 sm:p-6 pb-16 sm:pb-24">
      <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
        {/* Back + prev/next */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white text-sm transition-colors flex items-center gap-2"
          >
            &larr; Tilbake
          </button>
          {siblings.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => prevSibling && navigate(`/dashboard/campaign/${prevSibling.id}`)}
                disabled={!prevSibling}
                className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title={prevSibling?.title}
              >
                &larr; Forrige
              </button>
              <span className="text-white/40 text-xs tabular-nums">
                {currentIndex + 1} / {siblings.length}
              </span>
              <button
                onClick={() => nextSibling && navigate(`/dashboard/campaign/${nextSibling.id}`)}
                disabled={!nextSibling}
                className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title={nextSibling?.title}
              >
                Neste &rarr;
              </button>
            </div>
          )}
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded shrink-0">
                {TYPE_LABELS[campaign.type]}
              </span>
              <span className={`flex items-center gap-1.5 text-xs font-semibold ${isClosed ? 'text-red-400' : 'text-green-400'}`}>
                <span className={`w-2 h-2 rounded-full ${isClosed ? 'bg-red-500' : 'bg-green-400'}`} />
                {isClosed ? 'Stengt' : 'Åpen'}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-white leading-tight">{campaign.title}</h1>
            {campaign.description && (
              <p className="text-gray-400 mt-2 text-sm leading-relaxed">{campaign.description}</p>
            )}
          </div>
        </div>

        {/* Campaign URL + QR code */}
        <div className="bg-[#1a1a1a] rounded-xl p-4">
          <p className="text-gray-500 text-xs mb-1">Offentlig lenke</p>
          <p className="text-white text-sm font-mono break-all">{campaignUrl}</p>
          <button
            onClick={() => navigator.clipboard.writeText(campaignUrl)}
            className="mt-2 text-xs text-blue-400 hover:text-blue-300"
          >
            Kopier lenke
          </button>
        </div>

        {/* QR Code — én per lokasjon (kun demokratiuka-plakater med lokasjons-tracking) */}
        {currentAccount.handle === 'demokratiuka' && campaign.type === 'poster' && !SINGLE_QR_POSTER_SLUGS.has(campaign.slug) && (
          <div className="bg-[#1a1a1a] rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-gray-500 text-xs">QR-kode per lokasjon</p>
              <p className="text-gray-500 text-xs">{DEMOKRATIUKA_LOCATIONS.length} steder</p>
            </div>

            {/* Lokasjons-tabs */}
            <div className="flex flex-wrap gap-1.5">
              {DEMOKRATIUKA_LOCATIONS.map(loc => {
                const isActive = qrLocation === loc.slug;
                return (
                  <button
                    key={loc.slug}
                    onClick={() => setQrLocation(loc.slug)}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                      isActive
                        ? 'bg-white text-black font-medium'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {loc.label}
                  </button>
                );
              })}
            </div>

            {/* Aktiv QR */}
            <div className="flex justify-center bg-white p-4 rounded-lg">
              <img
                src={`/demokratiuka/qr/${campaign.slug}__${qrLocation}.png`}
                alt={`QR-kode for ${campaign.title} — ${getLocationLabel(qrLocation)}`}
                className="w-48 h-48 sm:w-56 sm:h-56 object-contain"
              />
            </div>

            <p className="text-gray-500 text-xs font-mono break-all">
              /request/demokratiuka/{campaign.slug}?loc={qrLocation}
            </p>

            <a
              href={`/demokratiuka/qr/${campaign.slug}__${qrLocation}.png`}
              download={`demokratiuka-qr-${campaign.slug}-${qrLocation}.png`}
              className="block w-full text-center bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
            >
              Last ned QR — {getLocationLabel(qrLocation)}
            </a>
          </div>
        )}

        {/* Enkelt-QR (debatter + spesialplakater uten lokasjon) */}
        {currentAccount.handle === 'demokratiuka' && (campaign.type === 'debate' || (campaign.type === 'poster' && SINGLE_QR_POSTER_SLUGS.has(campaign.slug))) && (() => {
          const hasRedVariant = RED_VARIANT_SLUGS.has(campaign.slug);
          const isRed = hasRedVariant && qrColor === 'red';
          const qrFile = isRed ? `${campaign.slug}--rod.png` : `${campaign.slug}.png`;
          const downloadName = isRed
            ? `demokratiuka-qr-${campaign.slug}-rod.png`
            : `demokratiuka-qr-${campaign.slug}.png`;
          return (
            <div className="bg-[#1a1a1a] rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-gray-500 text-xs">QR-kode</p>
                {hasRedVariant && (
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setQrColor('blue')}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                        !isRed ? 'bg-white text-black font-medium' : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      Blå
                    </button>
                    <button
                      onClick={() => setQrColor('red')}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                        isRed ? 'bg-white text-black font-medium' : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      Rød
                    </button>
                  </div>
                )}
              </div>
              <div className="flex justify-center bg-white p-4 rounded-lg mb-3">
                <img
                  src={`/demokratiuka/qr/${qrFile}`}
                  alt={`QR-kode for ${campaign.title}`}
                  className="w-48 h-48 sm:w-56 sm:h-56 object-contain"
                />
              </div>
              <a
                href={`/demokratiuka/qr/${qrFile}`}
                download={downloadName}
                className="block w-full text-center bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
              >
                Last ned QR-kode{hasRedVariant ? ` (${isRed ? 'rød' : 'blå'})` : ''}
              </a>
            </div>
          );
        })()}

        {/* Palmesus: QR + plakat */}
        {currentAccount.handle === 'palmesus' && (
          <div className="bg-[#1a1a1a] rounded-xl p-4 space-y-3">
            <p className="text-gray-500 text-xs mb-1">QR-kode</p>
            <div className="flex justify-center p-4 rounded-lg" style={{ backgroundColor: '#FFD9EC' }}>
              <img
                src={`/palmesus/qr/${campaign.slug}.png`}
                alt={`QR-kode for ${campaign.title}`}
                className="w-48 h-48 sm:w-56 sm:h-56 object-contain"
              />
            </div>
            <p className="text-gray-500 text-xs font-mono break-all">
              /request/palmesus/{campaign.slug}
            </p>
            <a
              href={`/palmesus/qr/${campaign.slug}.png`}
              download={`palmesus-qr-${campaign.slug}.png`}
              className="block w-full text-center bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
            >
              Last ned QR-kode
            </a>
            <button
              onClick={() => window.open(`/poster/palmesus/${campaign.slug}`, '_blank', 'noopener,noreferrer')}
              className="block w-full text-center text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
              style={{ backgroundColor: '#e00087' }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#7a0646')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#e00087')}
            >
              Åpne A4-plakat
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={startEdit}
            className="bg-white/10 hover:bg-white/20 text-white text-sm"
          >
            Rediger
          </Button>
          <Button
            onClick={handleToggleClose}
            className={`text-sm ${isClosed ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}
          >
            {isClosed ? 'Åpne' : 'Steng'}
          </Button>
          <Button
            onClick={handleDelete}
            className="bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm"
          >
            Slett
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button
              onClick={() => setFullscreen(true)}
              className="bg-white/10 hover:bg-white/20 text-white text-sm"
            >
              Fullskjerm
            </Button>
            <Button
              onClick={() => window.open(campaignUrl, '_blank')}
              className="bg-white/10 hover:bg-white/20 text-white text-sm"
            >
              Forhåndsvis
            </Button>
          </div>
        </div>

        {/* Edit form */}
        {isEditing && (
          <div className="bg-[#1a1a1a] rounded-xl p-5 space-y-3">
            <p className="text-white font-semibold text-sm">Rediger kampanje</p>
            <input
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              className="w-full p-3 rounded-lg bg-[#2a2a2a] text-white border-none focus:ring-2 focus:ring-white/30 text-sm"
              placeholder="Tittel"
            />
            <textarea
              value={editDesc}
              onChange={e => setEditDesc(e.target.value)}
              rows={3}
              className="w-full p-3 rounded-lg bg-[#2a2a2a] text-white border-none focus:ring-2 focus:ring-white/30 text-sm resize-none"
              placeholder="Beskrivelse (valgfritt)"
            />
            {campaign.type === 'vote' && (
              <div>
                <p className="text-gray-400 text-xs mb-2">Alternativer (ett per linje):</p>
                <textarea
                  value={editOptions}
                  onChange={e => setEditOptions(e.target.value)}
                  rows={4}
                  className="w-full p-3 rounded-lg bg-[#2a2a2a] text-white border-none focus:ring-2 focus:ring-white/30 text-sm resize-none"
                />
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-white text-black hover:bg-gray-200 text-sm"
              >
                {isSaving ? 'Lagrer...' : 'Lagre'}
              </Button>
              <Button
                onClick={() => setIsEditing(false)}
                className="bg-white/10 hover:bg-white/20 text-white text-sm"
              >
                Avbryt
              </Button>
            </div>
          </div>
        )}

        {/* Results */}
        <div className="bg-[#1a1a1a] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4 gap-2">
            {/* Venstre: destruktiv handling — separert fra Oppdater for å
                unngå utilsiktet trykk. */}
            <div className="flex-shrink-0">
              {suggestions.length > 0 ? (
                <Button
                  onClick={handleDeleteAllSuggestions}
                  className="bg-red-500/10 hover:bg-red-500/30 text-red-400 text-xs"
                >
                  Slett alle
                </Button>
              ) : (
                <span aria-hidden="true" />
              )}
            </div>
            <h2 className="text-lg font-semibold text-white">
              {campaign.type === 'vote' ? 'Stemmer' : 'Svar'}
              <span className="text-white/40 font-normal ml-2">
                ({filteredSuggestions.length}
                {activeLocation !== null && suggestions.length !== filteredSuggestions.length && (
                  <span className="text-white/30"> av {suggestions.length}</span>
                )}
                )
              </span>
            </h2>
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="bg-white/10 hover:bg-white/20 text-white text-xs"
            >
              {isRefreshing ? 'Laster...' : 'Oppdater'}
            </Button>
          </div>

          {/* Lokasjonsfilter — kun for demokratiuka-plakater med lokasjons-tracking */}
          {currentAccount.handle === 'demokratiuka' && campaign.type === 'poster' && !SINGLE_QR_POSTER_SLUGS.has(campaign.slug) && (
            <div className="flex flex-wrap gap-1.5 mb-4 pb-4 border-b border-white/5">
              <button
                onClick={() => setActiveLocation(null)}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                  activeLocation === null
                    ? 'bg-white text-black font-medium'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                Alle ({suggestions.length})
              </button>
              {DEMOKRATIUKA_LOCATIONS.map(loc => {
                const count = locationCounts[loc.slug] ?? 0;
                const isActive = activeLocation === loc.slug;
                return (
                  <button
                    key={loc.slug}
                    onClick={() => setActiveLocation(loc.slug)}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                      isActive
                        ? 'bg-white text-black font-medium'
                        : count > 0
                          ? 'bg-white/10 text-white/70 hover:bg-white/20'
                          : 'bg-white/5 text-white/30 hover:bg-white/10'
                    }`}
                  >
                    {loc.label} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {campaign.type === 'vote' ? (
            <VoteTally suggestions={filteredSuggestions} />
          ) : (
            <SuggestionsList
              suggestions={filteredSuggestions}
              onToggleUsed={handleToggleUsed}
              onDelete={handleDeleteSuggestion}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Vote tally sub-component ─── */
const VoteTally: React.FC<{ suggestions: Suggestion[] }> = ({ suggestions }) => {
  const tally: Record<string, number> = {};
  for (const s of suggestions) {
    tally[s.message] = (tally[s.message] || 0) + 1;
  }
  const entries = Object.entries(tally).sort((a, b) => b[1] - a[1]);
  const total = suggestions.length;

  if (entries.length === 0) {
    return <p className="text-gray-500 text-sm">Ingen stemmer ennå.</p>;
  }

  return (
    <div className="space-y-3">
      {entries.map(([label, count]) => {
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={label}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-white font-medium">{label}</span>
              <span className="text-gray-400">{count} ({pct}%)</span>
            </div>
            <div className="h-2 bg-[#2a2a2a] rounded-full overflow-hidden">
              <div
                className="h-full bg-white/70 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Suggestions list sub-component ─── */
interface SuggestionsListProps {
  suggestions: Suggestion[];
  onToggleUsed: (s: Suggestion) => void;
  onDelete: (s: Suggestion) => void;
}

const SuggestionsList: React.FC<SuggestionsListProps> = ({ suggestions, onToggleUsed, onDelete }) => {
  const formatTime = (createdAt: string) =>
    new Date(createdAt).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });
  const formatDate = (createdAt: string) =>
    new Date(createdAt).toLocaleDateString('no-NO', { day: 'numeric', month: 'short' });

  if (suggestions.length === 0) {
    return <p className="text-gray-500 text-sm">Ingen svar ennå.</p>;
  }

  return (
    <div className="space-y-2">
      {suggestions.map((s, i) => (
        <div
          key={s.id}
          className={`p-4 rounded-lg transition-colors ${s.used ? 'bg-[#1f2a1f] border border-green-500/20' : 'bg-[#2a2a2a]'}`}
        >
          <div className="flex items-start gap-3">
            <span className="text-white/30 text-xs shrink-0 mt-0.5">#{suggestions.length - i}</span>
            <div className="min-w-0 flex-1">
              <p className={`text-sm leading-relaxed ${s.used ? 'text-gray-400' : 'text-white'}`}>
                {s.message}
              </p>
              <p className="text-gray-500 text-xs mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                {s.submittedBy && <span className="text-gray-400">{s.submittedBy}</span>}
                {s.submittedBy && <span>&middot;</span>}
                <span>{formatDate(s.createdAt)}</span>
                <span>&middot;</span>
                <span>{formatTime(s.createdAt)}</span>
                {s.location && (
                  <>
                    <span>&middot;</span>
                    <span className="text-blue-300 bg-blue-500/10 px-1.5 py-0.5 rounded text-[11px]">
                      {getLocationLabel(s.location)}
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => onToggleUsed(s)}
                className={`text-xs px-2.5 py-1 rounded transition-colors ${
                  s.used
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                }`}
              >
                {s.used ? 'Brukt' : 'Merk brukt'}
              </button>
              <button
                onClick={() => onDelete(s)}
                className="text-xs px-2 py-1 rounded bg-white/5 text-white/30 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                title="Slett"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
